import * as React from 'react';
import { Building2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { extractAddressComplementParts } from '@klareospace/pharos-lib-front-utils';
import type { Customer } from '@klareospace/pharos-lib-types';

import type { AddressBookEntryInput } from '&/features/customers';
import { AddressComplementParts } from '&/features/orders/components';

import { formatRouteAddress } from '../../utils';
import type { RouteInformationContact } from '../../utils';
import { ServiceTimeField } from '../service-time-field';
import { DeliveryAddressPicker } from './delivery-address-picker';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DeliveryBlockProps {
  /** The selected customer — drives the inline picker's row list; the picker only renders while editing and a customer is selected. */
  customer: Customer | null;
  deliveryAddress: AddressBookEntryInput | null;
  deliveryContact: RouteInformationContact | null;
  deliveryDurationTime: number;
  isAddressEditing: boolean;
  onAddNewAddress: () => void;
  onCancelEditingAddress: () => void;
  onEditAddressEntry: (index: number) => void;
  onSelectAddress: (index: number) => void;
  onServiceTimeChange: (value: number) => void;
  onStartEditingAddress: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Delivery" block of the Route information section — customer address recap, service time, and
 * (while `isAddressEditing`) the inline `DeliveryAddressPicker` rendered below the recap, which
 * always stays visible.
 */
export function DeliveryBlock({
  customer,
  deliveryAddress,
  deliveryContact,
  deliveryDurationTime,
  isAddressEditing,
  onAddNewAddress,
  onCancelEditingAddress,
  onEditAddressEntry,
  onSelectAddress,
  onServiceTimeChange,
  onStartEditingAddress,
}: DeliveryBlockProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');

  return (
    <div className="space-y-3 border-t pt-6">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Building2 aria-hidden="true" className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">{tCommon('fields.delivery')}</span>
      </div>

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          {deliveryAddress ? (
            <>
              <p className="truncate text-sm font-medium text-foreground">
                {formatRouteAddress(deliveryAddress.address)}
              </p>
              <AddressComplementParts parts={extractAddressComplementParts(deliveryAddress.address)} />
              {deliveryContact?.name && <p className="truncate text-sm text-foreground">{deliveryContact.name}</p>}
              {deliveryContact?.phone && (
                <p className="truncate text-sm text-muted-foreground">{deliveryContact.phone}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('sections.routeInformation.delivery.noAddress')}</p>
          )}
        </div>
        <button
          aria-label={tCommon('fields.editDeliveryAddress')}
          className="shrink-0 cursor-pointer rounded-md p-1 text-primary hover:bg-muted hover:text-primary/80"
          onClick={onStartEditingAddress}
          type="button"
        >
          <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>

      {isAddressEditing && customer && (
        <DeliveryAddressPicker
          addresses={customer.addresses}
          customer={customer}
          onAddNew={onAddNewAddress}
          onCancel={onCancelEditingAddress}
          onEdit={onEditAddressEntry}
          onSelect={onSelectAddress}
          selectedEntry={deliveryAddress}
          selectedIndex={deliveryAddress?.sourceIndex}
        />
      )}

      <ServiceTimeField
        confirmAriaLabel={tCommon('actions.confirm')}
        displayValue={t('serviceTimeValue', {
          unit: tCommon('fields.durationUnit'),
          value: deliveryDurationTime,
        })}
        editAriaLabel={t('sections.routeInformation.delivery.serviceTime.edit')}
        inputAriaLabel={t('sections.routeInformation.delivery.serviceTime.ariaLabel')}
        label={t('common.serviceTimeLabel')}
        onChange={onServiceTimeChange}
        unitLabel={tCommon('fields.durationUnit')}
        value={deliveryDurationTime}
      />
    </div>
  );
}
